import { useId, useState } from 'react'
import { AutoplayOnView } from '@educator/components/AutoplayOnView'
import { Image } from '@educator/components/Image'
import { useFireEvent } from '@educator/tracking/educatorTracking'
import { Button } from '@mc/educator-design-system'
import { Video } from '@mc/video'
import type { PeekSide, ValuePromiseItem, ValuePromiseMedia } from './types'

export interface ValuePromiseCardProps extends ValuePromiseItem {
  /** Reported as tile_position_index on view-more. */
  positionIndex: number
  /** Edge this card is clipped by; drives the fade overlay. */
  peekSide: PeekSide
  viewMoreLabel: string
  closeLabel: string
}

const CardMedia = ({ media }: { media: ValuePromiseMedia }) => {
  if (media.kind === 'video') {
    return (
      <>
        <Video
          mediaUuid={media.videoUuid}
          className='h-full w-full object-cover'
          playsInline
          fit='cover'
          autoPlay
          muted
          controls={false}
          loop
          exemptSingleton
        >
          <AutoplayOnView />
        </Video>
        {/* Swallow clicks so double-click fullscreen can't fire. */}
        <div className='absolute inset-0 z-10' aria-hidden='true' />
      </>
    )
  }

  return (
    <Image
      src={media.imageUrl}
      alt=''
      fill
      className='object-cover'
      sizes='(max-width: 768px) 288px, 360px'
    />
  )
}

/** Expanded (Figma Variant2) swaps the art for the card's solid brand color. */
export const ValuePromiseCard = ({
  headline,
  body,
  media,
  expandedBgClass,
  positionIndex,
  peekSide,
  viewMoreLabel,
  closeLabel,
}: ValuePromiseCardProps) => {
  const [expanded, setExpanded] = useState(false)
  const bodyId = useId()
  const fireEvent = useFireEvent()

  // Fire on expand only, not collapse.
  const toggleExpanded = () => {
    if (!expanded) {
      fireEvent('educatorHubClick', {
        action: 'view_more',
        tile_title: headline,
        tile_position_index: positionIndex,
      })
    }
    setExpanded((prev) => !prev)
  }

  return (
    <article
      aria-label={headline}
      className={`relative flex h-[544px] w-[288px] shrink-0 snap-start flex-col overflow-hidden md:w-[360px] ${
        expanded ? expandedBgClass : 'bg-black'
      }`}
    >
      {/* Art + shade only while collapsed (Figma). */}
      {!expanded && (
        <>
          <div className='absolute inset-0'>
            <CardMedia media={media} />
          </div>
          {/* Keeps the copy legible over the image. */}
          <div
            aria-hidden='true'
            className='absolute inset-x-0 bottom-0 z-10 h-[319px] bg-gradient-to-b from-transparent to-black/65'
          />
          {/* Fade a clipped card into the page bg toward its cut edge. */}
          {peekSide && (
            <div
              aria-hidden='true'
              className={`pointer-events-none absolute inset-0 z-30 ${
                peekSide === 'right' ? 'bg-gradient-to-r' : 'bg-gradient-to-l'
              } from-edu-bg-primary/40 to-edu-bg-primary`}
            />
          )}
        </>
      )}

      <div
        className={`relative z-20 flex flex-col gap-6 p-6 text-edu-fg-primary md:p-8 ${
          expanded ? 'flex-1 justify-between' : 'mt-auto'
        }`}
      >
        <div className='flex flex-col gap-4'>
          {/* 24/28px per Figma. */}
          <h3 className='m-0 text-[24px] font-normal leading-[1.1] md:text-[28px]'>
            {headline}
          </h3>
          <p
            id={bodyId}
            className={`m-0 text-body-2 leading-body-2 ${
              expanded ? '' : 'line-clamp-3'
            }`}
          >
            {body}
          </p>
        </div>
        {/* border-white/50 + uppercase are deliberate Boogie deviations to match
            Figma's secondary button over media (no stroke token / caps yet). */}
        <Button
          kind='secondary'
          size='small'
          className='h-10 self-start border border-white/50 bg-transparent uppercase hover:bg-white/10'
          aria-expanded={expanded}
          aria-controls={bodyId}
          onClick={toggleExpanded}
        >
          {expanded ? closeLabel : viewMoreLabel}
        </Button>
      </div>
    </article>
  )
}
